import React from 'react'
import '../common/react-legacy'
import { createRoot } from 'react-dom/client'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom'
import { Provider } from 'react-redux'

import '../common/globals'

import Layout from '../common/components/layout/Layout'
import Login from '../common/components/login/Login'
import ForgotPassword from '../common/components/login/ForgotPassword'
import ResetPassword from '../common/components/login/ResetPassword'
import RetailerList from './components/retailer/List'
import UserList from './components/users/List'
import RetailerForm from '../common/components/retailer/Form'

import FeaturedList from './components/featured/List'
import DesignsCollections from './components/designs-and-collections/List'
import ProductReview from './components/product-review/List'
import SingleProdReview from './components/product-review/SingleProdReview'
import CommentingSystem from './components/commenting-system/List'
import PromotionSettings from './components/promotion-settings/List'

import store from '../common/store'
import { setNextUrl, setCurrentUser } from '../common/actions/login'
import auth from '../common/auth'
import { DiamondRoutes, AccountRoutes } from '../common/routes'
import NavigationActions from '../components/layout/navigation/actions/NavigationActions'
import menu from './menu.json'
import CommenList from './components/commenting-system/CommenList'

const appName = 'Admin'
const dashboardUrl = '/retailers?page=1'

/** Redirect already-logged-in users away from the login page */
function PublicRoute({ children }) {
  if (auth.loggedIn()) return <Navigate to={dashboardUrl} replace />
  return children
}

/** Guard all protected routes; render Layout with nested page via <Outlet> */
function ProtectedLayout() {
  if (!auth.loggedIn()) return <Navigate to="/login" replace />
  return (
    <Layout appName={appName} menu={menu}>
      <Outlet />
    </Layout>
  )
}

store.dispatch(setNextUrl(dashboardUrl))
NavigationActions.initWithRawItems(menu.items)

// The first paint waits for auth.init() so routing starts from the right
// signed-in state, but until something renders the admin is a white page, so it
// must never wait forever. init() bounds its own session check; this deadline
// is the backstop for the rest of it (a seller's profile fetch) and the catch
// covers a throw anywhere in the chain. Both fall back to the cookie hint, the
// same call init() makes when the session check fails without a 401.
const AUTH_INIT_DEADLINE_MS = 15000

function withDeadline(promise, ms, fallback) {
  let timer
  const deadline = new Promise(resolve => {
    timer = setTimeout(() => resolve(fallback()), ms)
  })
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer))
}

withDeadline(auth.init(), AUTH_INIT_DEADLINE_MS, () => auth.currentUser())
  .catch(error => {
    // eslint-disable-next-line no-console
    console.error('Auth init failed; starting from the cookie state.', error)
    return auth.currentUser()
  })
  .then(currentUser => {
    try {
      store.dispatch(setCurrentUser(currentUser))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Could not store the current user.', error)
    }
    renderApp()
  })

function renderApp() {
  const root = createRoot(document.getElementById('smartadmin-root'))
  root.render(
    <Provider store={store}>
      <BrowserRouter>
        <Routes>
          <Route path="/">
            {/* Public: login */}
            <Route
              path="login"
              element={<PublicRoute><Login appName={appName} /></PublicRoute>}
            />
            <Route
              path="forgot-password"
              element={<PublicRoute><ForgotPassword appName={appName} /></PublicRoute>}
            />
            <Route
              path="reset-password"
              element={<PublicRoute><ResetPassword appName={appName} /></PublicRoute>}
            />

            {/* Protected: all app pages wrapped in Layout */}
            <Route element={<ProtectedLayout />}>
              <Route path="retailers">
                <Route index element={<RetailerList />} />
                <Route path=":createdDay/:retailerId/edit" element={<RetailerForm />} />
                <Route path="new" element={<RetailerForm />} />
              </Route>

              <Route path="users">
                <Route index element={<UserList />} />
                <Route path=":createdDay/:retailerId/edit" element={<RetailerForm />} />
                <Route path="new" element={<RetailerForm />} />
              </Route>

              <Route path="featuredlist" element={<FeaturedList />} />
              <Route path="designs-&-collections" element={<DesignsCollections />} />

              <Route path="productReview" element={<ProductReview />} />
              <Route path="product-review/:id" element={<SingleProdReview />} />

              <Route path="commentingSystem" element={<CommentingSystem />} />
              <Route path="comment/:id" element={<CommenList />} />
              <Route path="promotion-settings" element={<PromotionSettings />} />

              {DiamondRoutes}
              {AccountRoutes}
            </Route>

            {/* Default: redirect to login */}
            <Route index element={<Navigate to="login" replace />} />
            <Route path="*" element={<Navigate to="login" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </Provider>
  )
}
